"use client";

import * as stylex from "@stylexjs/stylex";
import { colors } from "@/colors.stylex";
import { useMokaMatchContext } from "@/components/moka-match/moka-match-root";
import { MOKA_MATCH_PERCENT_MULTIPLIER } from "@/constants";

const styles = stylex.create({
  debugPanel: {
    display: "grid",
    gridTemplateColumns: {
      default: "repeat(2, minmax(0, 1fr))",
      "@media (min-width: 640px)": "repeat(4, minmax(0, 1fr))",
    },
    columnGap: "1rem",
    rowGap: "0.75rem",
    borderRadius: "0.75rem",
    backgroundColor: `color-mix(in oklab, ${colors.neutral100} 70%, transparent)`,
    padding: "0.75rem",
    fontFamily: "var(--font-inter)",
    fontVariantNumeric: "tabular-nums",
  },
  debugStat: {
    minWidth: 0,
  },
  debugStatLabel: {
    fontSize: "0.75rem",
    lineHeight: "1rem",
    fontWeight: 400,
    color: colors.neutral500,
  },
  debugStatValue: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    fontSize: "0.875rem",
    lineHeight: "1.25rem",
    fontWeight: 400,
    color: colors.neutral900,
  },
});

const getPlayedStoneCount = (gameState: MokaGameState, color: "black" | "white") =>
  gameState.moveHistory.filter((move) => move.color === color && move.coordinate !== null).length;

const MokaMatchDebugPanel = () => {
  const { areaAnalysis, isDebugMode, position } = useMokaMatchContext();

  if (!isDebugMode) {
    return null;
  }

  const { gameState } = position;
  const blackWinPercentage = Math.round(
    position.blackWinProbability * MOKA_MATCH_PERCENT_MULTIPLIER,
  );
  const whiteWinPercentage = MOKA_MATCH_PERCENT_MULTIPLIER - blackWinPercentage;
  const areaLead =
    areaAnalysis.score > 0
      ? `Black +${areaAnalysis.score}`
      : areaAnalysis.score < 0
        ? `White +${Math.abs(areaAnalysis.score)}`
        : "Even";
  const blackCapturedStoneCount =
    getPlayedStoneCount(gameState, "black") - areaAnalysis.blackStoneCount;
  const whiteCapturedStoneCount =
    getPlayedStoneCount(gameState, "white") - areaAnalysis.whiteStoneCount;
  const passMoveCount = gameState.moveHistory.filter((move) => move.coordinate === null).length;
  const debugStats: MokaMatchDebugStat[] = [
    {
      label: "Move",
      value: `${gameState.moveCount} · ${gameState.nextColor === "black" ? "Black" : "White"} next`,
    },
    { label: "Area now", value: `${areaLead} incl. komi` },
    {
      label: "Black",
      value: `${areaAnalysis.blackStoneCount} stones · ${areaAnalysis.blackTerritoryCoordinates.length} territory`,
    },
    {
      label: "White",
      value: `${areaAnalysis.whiteStoneCount} stones · ${areaAnalysis.whiteTerritoryCoordinates.length} territory`,
    },
    { label: "Value", value: `B ${blackWinPercentage}% · W ${whiteWinPercentage}%` },
    { label: "Neutral", value: `${areaAnalysis.neutralPointCount} points` },
    { label: "Captured", value: `B ${blackCapturedStoneCount} · W ${whiteCapturedStoneCount}` },
    {
      label: "Passes",
      value: `${passMoveCount} total · ${gameState.consecutivePassCount} consecutive`,
    },
  ];

  return (
    <dl aria-label="Moka debug analysis" {...stylex.props(styles.debugPanel)}>
      {debugStats.map((debugStat) => (
        <div key={debugStat.label} {...stylex.props(styles.debugStat)}>
          <dt {...stylex.props(styles.debugStatLabel)}>{debugStat.label}</dt>
          <dd {...stylex.props(styles.debugStatValue)}>{debugStat.value}</dd>
        </div>
      ))}
    </dl>
  );
};

MokaMatchDebugPanel.displayName = "MokaMatchDebugPanel";

export { MokaMatchDebugPanel };
